// src/pages/finance-portal/leases/LeaseDetail.js
//
// Lease detail with classification reasoning, ROU/liability KPIs,
// amortization schedule, and record-payment workflow.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    MenuItem, Select, FormControl, InputLabel, InputAdornment, List, ListItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CategoryIcon from '@mui/icons-material/Category';
import PaymentIcon from '@mui/icons-material/Payment';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_COLORS = {
    DRAFT:      { bg: '#f1f5f9', color: '#64748b' },
    ACTIVE:     { bg: '#dcfce7', color: '#166534' },
    EXPIRED:    { bg: '#e0e7ff', color: '#4338ca' },
    TERMINATED: { bg: '#fecaca', color: '#991b1b' },
};

const CLASSIFICATION_COLORS = {
    OPERATING:  { bg: '#dbeafe', color: '#1d4ed8' },
    FINANCE:    { bg: '#fef3c7', color: '#a16207' },
    SHORT_TERM: { bg: '#f1f5f9', color: '#475569' },
    LOW_VALUE:  { bg: '#f1f5f9', color: '#475569' },
};

const PAYMENT_STATUS_COLORS = {
    SCHEDULED: { bg: '#f1f5f9', color: '#64748b' },
    PAID:      { bg: '#dcfce7', color: '#166534' },
    OVERDUE:   { bg: '#fecaca', color: '#991b1b' },
    SKIPPED:   { bg: '#f1f5f9', color: '#94a3b8' },
};

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 2,
    }).format(val || 0);
}

export default function LeaseDetail() {
    const { leaseId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [lease, setLease] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [reclassifyDialog, setReclassifyDialog] = useState({
        open: false, mode: 'auto', override: '', reason: '',
    });

    const [payDialog, setPayDialog] = useState({
        open: false, payment: null,
        paid_date: '', actual_paid: '', payment_reference: '', notes: '',
    });

    const fetchLease = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/leases/${leaseId}`);
            if (res.data.success) setLease(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [leaseId]);

    useEffect(() => { fetchLease(); }, [fetchLease]);

    const recompute = async () => {
        if (!window.confirm('Recompute the entire schedule? Only allowed if no payments are recorded.')) return;
        try {
            const res = await axiosInstance.post(`/api/finance/leases/${leaseId}/recompute`);
            enqueueSnackbar(res.data?.message || 'Recomputed', { variant: 'success' });
            fetchLease();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const submitReclassify = async () => {
        try {
            const body = reclassifyDialog.mode === 'override'
                ? { override: reclassifyDialog.override, reason: reclassifyDialog.reason || null }
                : {};
            const res = await axiosInstance.post(
                `/api/finance/leases/${leaseId}/reclassify`, body);
            enqueueSnackbar(res.data?.message || 'Reclassified', { variant: 'success' });
            setReclassifyDialog({ ...reclassifyDialog, open: false });
            fetchLease();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const terminate = async () => {
        if (!window.confirm('Terminate this lease? It becomes read-only.')) return;
        try {
            await axiosInstance.post(`/api/finance/leases/${leaseId}/terminate`);
            enqueueSnackbar('Lease terminated', { variant: 'success' });
            fetchLease();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const openPay = (payment) => setPayDialog({
        open: true, payment,
        paid_date: new Date().toISOString().slice(0, 10),
        actual_paid: String(payment.cash_payment),
        payment_reference: '', notes: '',
    });

    const submitPay = async () => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/leases/${leaseId}/payments/${payDialog.payment.id}/record`,
                {
                    paid_date: payDialog.paid_date,
                    actual_paid: parseFloat(payDialog.actual_paid),
                    payment_reference: payDialog.payment_reference || null,
                    notes: payDialog.notes || null,
                },
            );
            enqueueSnackbar(res.data?.message || 'Payment recorded', { variant: 'success' });
            setPayDialog({ ...payDialog, open: false });
            fetchLease();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;
    if (!lease) return (
        <Box sx={{ p: 4, fontFamily }}>
            <Alert severity="error">{error || 'Lease not found'}</Alert>
            <Button onClick={() => navigate('/finance-portal/leases')} sx={{ mt: 2, fontFamily }}>
                Back to list
            </Button>
        </Box>
    );

    const sStyle = STATUS_COLORS[lease.status] || STATUS_COLORS.ACTIVE;
    const cStyle = CLASSIFICATION_COLORS[lease.classification] || CLASSIFICATION_COLORS.OPERATING;
    const isReadOnly = lease.status === 'TERMINATED' || lease.status === 'EXPIRED';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/leases')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            {lease.lease_number} · {lease.lessor}
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {lease.name}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={lease.status} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: sStyle.bg, color: sStyle.color }} />
                    <Chip label={lease.classification?.replace('_', ' ')} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: cStyle.bg, color: cStyle.color }} />
                    {lease.classification_overridden && (
                        <Chip label="Manually Overridden" size="small"
                              sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                    bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                    )}
                    <Chip label={`${lease.term_months}mo @ ${lease.discount_rate_pct}%`} size="small"
                          sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* KPI cards */}
                <Box sx={{ display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                          gap: 2, mb: 3 }}>
                    {[
                        { label: 'Initial ROU Asset',
                          value: fmtMoney(lease.rou_asset_initial, lease.currency), color: '#0f172a' },
                        { label: 'Initial Liability',
                          value: fmtMoney(lease.lease_liability_initial, lease.currency), color: '#0f172a' },
                        { label: 'Current ROU',
                          value: fmtMoney(lease.rou_asset_current, lease.currency), color: '#16a34a' },
                        { label: 'Current Liability',
                          value: fmtMoney(lease.lease_liability_current, lease.currency), color: '#dc2626' },
                    ].map(k => (
                        <Card key={k.label} sx={{ borderRadius: '14px', boxShadow: 'none',
                                                   border: '1px solid #e2e8f0', p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase', fontWeight: 600 }}>
                                {k.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.3rem',
                                              color: k.color, mt: 0.25 }}>
                                {k.value}
                            </Typography>
                        </Card>
                    ))}
                </Box>

                {/* Action bar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {!isReadOnly && (
                        <Button startIcon={<EditIcon />} variant="outlined" size="small"
                                onClick={() => navigate(`/finance-portal/leases/${lease.id}/edit`)}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                            Edit
                        </Button>
                    )}
                    {!isReadOnly && (
                        <Button startIcon={<CategoryIcon />} variant="outlined" size="small"
                                onClick={() => setReclassifyDialog({
                                    open: true, mode: lease.classification_overridden ? 'override' : 'auto',
                                    override: lease.classification, reason: lease.classification_notes || '',
                                })}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                            Reclassify
                        </Button>
                    )}
                    {!isReadOnly && (
                        <Tooltip title="Wipe & rebuild the schedule">
                            <span>
                                <Button startIcon={<RefreshIcon />} variant="outlined" size="small"
                                        onClick={recompute}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                    Recompute
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {lease.status === 'ACTIVE' && (
                        <Button startIcon={<StopCircleIcon />} variant="text" size="small" onClick={terminate}
                                sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                            Terminate
                        </Button>
                    )}
                </Box>

                {/* Classification reasons */}
                {lease.classification_reasons && lease.classification_reasons.length > 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                                ASC 842 Classification Reasoning
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0 }}>
                            {lease.classification_reasons.map((r, i) => {
                                const Icon = r.result === true ? CheckCircleIcon
                                    : r.result === false ? CancelIcon : null;
                                const color = r.result === true ? '#16a34a'
                                    : r.result === false ? '#94a3b8' : '#64748b';
                                return (
                                    <ListItem key={i} sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: '100%' }}>
                                            {Icon && <Icon sx={{ color, fontSize: 18 }} />}
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem',
                                                                  color: '#1e293b' }}>
                                                    {r.test?.replace(/_/g, ' ')}
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b' }}>
                                                    {r.detail}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Card>
                )}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                            Amortization Schedule ({lease.payments?.length || 0} periods)
                        </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>#</TableCell>
                                    <TableCell sx={labelCell}>Due</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Cash</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Interest</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>ROU Amort.</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Lease Expense</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Liab. After</TableCell>
                                    <TableCell sx={{ ...labelCell, textAlign: 'right' }}>ROU After</TableCell>
                                    <TableCell sx={labelCell}>Status</TableCell>
                                    <TableCell sx={labelCell}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(lease.payments || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4,
                                            ...dataCell, color: '#94a3b8' }}>
                                            No schedule
                                        </TableCell>
                                    </TableRow>
                                ) : lease.payments.map(p => {
                                    const ps = PAYMENT_STATUS_COLORS[p.status] || PAYMENT_STATUS_COLORS.SCHEDULED;
                                    return (
                                        <TableRow key={p.id} hover>
                                            <TableCell sx={dataCell}>{p.payment_number}</TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>{p.due_date}</TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmtMoney(p.cash_payment, lease.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmtMoney(p.interest_expense, lease.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmtMoney(p.rou_amortization, lease.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                              fontWeight: 600, color: '#1e293b' }}>
                                                {fmtMoney(p.lease_expense, lease.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmtMoney(p.liability_balance_after, lease.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmtMoney(p.rou_balance_after, lease.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={p.status} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', height: 20,
                                                            bgcolor: ps.bg, color: ps.color }} />
                                            </TableCell>
                                            <TableCell>
                                                {p.status !== 'PAID' && !isReadOnly && (
                                                    <Button size="small" startIcon={<PaymentIcon />}
                                                            onClick={() => openPay(p)}
                                                            sx={{ fontFamily, textTransform: 'none', fontSize: '0.78rem' }}>
                                                        Record
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Reclassify dialog */}
            <Dialog open={reclassifyDialog.open}
                    onClose={() => setReclassifyDialog({ ...reclassifyDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Reclassify Lease</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <FormControl size="small">
                            <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Mode</InputLabel>
                            <Select value={reclassifyDialog.mode} label="Mode"
                                    onChange={e => setReclassifyDialog(p => ({ ...p, mode: e.target.value }))}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                <MenuItem value="auto" sx={{ fontFamily }}>Re-run ASC 842 tests automatically</MenuItem>
                                <MenuItem value="override" sx={{ fontFamily }}>Manual override</MenuItem>
                            </Select>
                        </FormControl>
                        {reclassifyDialog.mode === 'override' && (
                            <>
                                <FormControl size="small">
                                    <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Set classification</InputLabel>
                                    <Select value={reclassifyDialog.override} label="Set classification"
                                            onChange={e => setReclassifyDialog(p => ({ ...p, override: e.target.value }))}
                                            sx={{ fontFamily, borderRadius: '10px' }}>
                                        {['OPERATING', 'FINANCE', 'SHORT_TERM', 'LOW_VALUE'].map(c =>
                                            <MenuItem key={c} value={c} sx={{ fontFamily }}>{c.replace('_', ' ')}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField label="Reason / justification" size="small" multiline rows={2}
                                           value={reclassifyDialog.reason}
                                           onChange={e => setReclassifyDialog(p => ({ ...p, reason: e.target.value }))}
                                           InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                           InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReclassifyDialog({ ...reclassifyDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitReclassify}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#22c55e',
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Record-payment dialog */}
            <Dialog open={payDialog.open} onClose={() => setPayDialog({ ...payDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Record Payment {payDialog.payment ? `#${payDialog.payment.payment_number}` : ''}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <TextField label="Paid Date *" type="date" size="small"
                                   value={payDialog.paid_date}
                                   onChange={e => setPayDialog(p => ({ ...p, paid_date: e.target.value }))}
                                   InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Amount Paid *" size="small" type="number"
                                   value={payDialog.actual_paid}
                                   onChange={e => setPayDialog(p => ({ ...p, actual_paid: e.target.value }))}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: { fontFamily, fontSize: '0.85rem' },
                                   }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Reference" size="small"
                                   value={payDialog.payment_reference}
                                   onChange={e => setPayDialog(p => ({ ...p, payment_reference: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Notes" size="small" multiline rows={2}
                                   value={payDialog.notes}
                                   onChange={e => setPayDialog(p => ({ ...p, notes: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPayDialog({ ...payDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitPay}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#22c55e',
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Record
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
